import { PublicJobDto } from '@mi-front/api-clients';
import { Spinner } from '@mi-front/ui';
import { Box, Button, Chip } from '@mui/material';
import { UseQueryResult } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { ActionConfirmation } from './ActionConfirmation';

/**
 * Props for the JobStarter component
 * @template TData - The type of data returned by the query, defaults to PublicJobDto
 */
interface JobStarterProps<TData = PublicJobDto> {
  /** React query that returns a PublicJobDto with status */
  query: UseQueryResult<TData, Error>;
  /** Function to start the job */
  onStart: () => void | Promise<void>;
  /** Component to render when job is completed */
  successComponent: React.ReactElement;
  /** Component to render when job fails */
  errorComponent?: React.ReactNode;
  /** Message to show when job is in progress */
  progressMessage?: React.ReactNode;
  /** Button text for starting the job */
  startButtonText: React.ReactNode;
  /** Icon for the button and chips */
  icon?: React.ReactElement;
  /** Confirmation object with title and description */
  confirmation?: {
    title: React.ReactNode;
    description: React.ReactNode;
  };
  /** Custom wrapper for the component */
  wrapper?: (children: React.ReactNode) => React.ReactNode;
}

/**
 * JobStarter Component
 *
 * A reusable component that manages the lifecycle of asynchronous jobs. It provides
 * a unified interface for starting, monitoring, and displaying the status of jobs
 * that return a PublicJobDto with status information.
 *
 * The component handles:
 * - Job initiation with optional confirmation
 * - Progress tracking during job execution
 * - Success/failure state display
 * - Automatic loading states during job start
 * - Restart functionality for failed jobs
 *
 * @template TData - The type of data returned by the query, defaults to PublicJobDto
 *
 * @example
 * ```tsx
 * <JobStarter
 *   query={jobQuery}
 *   onStart={async () => await startJob()}
 *   successComponent={<Chip label="Job completed" color="success" />}
 *   errorComponent={<Chip label="Job failed" color="error" />}
 *   startButtonText="Start Job"
 *   progressMessage="Processing..."
 *   icon={<PlayIcon />}
 *   confirmation={{
 *     title: "Start Job",
 *     description: "Are you sure you want to start this job?"
 *   }}
 * />
 * ```
 */
export function JobStarter<TData = PublicJobDto>({
  query,
  onStart,
  successComponent,
  errorComponent,
  progressMessage,
  startButtonText,
  icon,
  confirmation,
}: JobStarterProps<TData>): React.ReactElement | null {
  const { $t } = useIntl();
  const displayProgressMessage =
    progressMessage ??
    $t({
      id: 'common.jobInProgress',
      defaultMessage: 'Job in progress',
    });
  // Track when the job is being started to show loading state
  const [isStarting, setIsStarting] = useState(false);

  const data = query.data as PublicJobDto;

  // Determine if job is in progress based on status or if we're currently starting
  const isInProgress = data?.status === 'WAITING' || data?.status === 'IN_PROGRESS' || isStarting;

  /**
   * Handles the job start process with proper loading state management
   * Sets isStarting to true before calling onStart and resets it when the job status changes
   */
  const handleStart = async () => {
    setIsStarting(true);

    try {
      await onStart();
    } catch (error) {
      console.error(error);
    }
  };

  // Reset isStarting when job status changes to avoid
  // the disable/enable glitch of the start button
  useEffect(() => {
    setIsStarting(false);
  }, [data?.status]);

  // Show spinner while query is loading or has error
  if (query.isLoading || query.isError) {
    return <Spinner />;
  }

  // Create the start button with optional confirmation dialog
  const startButton = confirmation ? (
    <ActionConfirmation
      title={confirmation.title}
      description={confirmation.description}
      onConfirm={handleStart}
      trigger={(onClick) => (
        <Button size="small" variant="outlined" color="info" startIcon={icon} onClick={onClick}>
          {startButtonText}
        </Button>
      )}
    />
  ) : (
    <Button size="small" variant="outlined" color="info" startIcon={icon} onClick={handleStart}>
      {startButtonText}
    </Button>
  );

  // Show loading button when job is in progress
  if (isInProgress) {
    return (
      <Button
        size="small"
        variant="outlined"
        color="info"
        loading
        startIcon={icon}
        loadingPosition="start"
      >
        {displayProgressMessage}{' '}
        {typeof data.progress === 'number' ? `(${Math.round(data.progress)}%)` : ''}
      </Button>
    );
  }

  // Show start button when no job data exists
  if (!data) {
    return startButton;
  }

  // Handle failed job state with restart option and error display
  if (data.status === 'FAILED') {
    const restartButton = confirmation ? (
      <ActionConfirmation
        title={confirmation.title}
        description={confirmation.description}
        onConfirm={handleStart}
        trigger={(onClick) => (
          <Button size="small" variant="outlined" color="info" startIcon={icon} onClick={onClick}>
            {startButtonText}
          </Button>
        )}
      />
    ) : (
      <Button size="small" variant="outlined" color="info" startIcon={icon} onClick={handleStart}>
        {startButtonText}
      </Button>
    );

    const errorDisplay = errorComponent || (
      <Chip
        icon={icon}
        size="small"
        label={$t({
          id: 'common.jobFailed',
          defaultMessage: 'Job failed',
        })}
        color="error"
      />
    );

    return (
      <Box>
        {restartButton}
        {errorDisplay}
      </Box>
    );
  }

  // Show success component when job is completed
  if (data.status === 'COMPLETED') {
    return successComponent;
  }

  // Default case: show start button for other states
  return startButton;
}
